import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useSiteContent, pickLocalized } from "@/contexts/SiteContentContext";
import { useDomain } from "@/hooks/useDomain";
import { SERVICE_DETAILS, SERVICE_SLUGS } from "@/lib/serviceDetails";
import { MessageCircle, Mail } from "lucide-react";

// Cross-brand network — shown on every domain so visitors can discover sister-sites.
const SISTER_DOMAINS = [
  { host: "digital-mallorca.de", label: "Mallorca Digital" },
  { host: "starlink-mallorca.de", label: "Starlink Mallorca" },
  { host: "internet-mallorca.de", label: "Internet Mallorca" },
  { host: "kameramann-mallorca.de", label: "Kameramann Mallorca" },
  { host: "streamsetup-mallorca.de", label: "Stream Setup Mallorca" },
  { host: "irlstreams-mallorca.de", label: "IRL Streams Mallorca" },
  { host: "onlinemarketing-mallorca.de", label: "Online Marketing Mallorca" },
];

const Footer = () => {
  const { t, locale } = useLanguage();
  const { content } = useSiteContent();
  const domain = useDomain();
  const contact = content?.contact || {};
  const phone = (contact.whatsapp_number || "+34657138915").replace(/[^+\d]/g, "");
  const waUrl = `https://wa.me/${phone.replace(/^\+/, "")}`;
  const email = contact.email || "info@digital-mallorca.de";
  const tagline = pickLocalized(content?.footer?.tagline, locale, t("footer.tagline"));

  const brandInitial = (domain.brandName || "Mallorca Digital").trim().charAt(0).toUpperCase();

  // On focused domains, the service column emphasises THIS brand's service first,
  // then the other 5 services as cross-sell. On the main brand, all services equal weight.
  const orderedServices = (() => {
    const focused = domain.focusedServiceSlug;
    if (!focused) return SERVICE_SLUGS;
    return [focused, ...SERVICE_SLUGS.filter((s) => s !== focused)];
  })();

  return (
    <footer
      data-testid="site-footer"
      className="bg-neutral-950 text-neutral-300"
    >
      <div className="container-px mx-auto max-w-7xl py-16 lg:py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-white text-neutral-950 grid place-items-center font-display font-semibold">
                {brandInitial}
              </div>
              <div className="leading-tight">
                <div
                  data-testid="footer-brand-name"
                  className="font-display text-white text-lg"
                >
                  {domain.brandName}
                </div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-sky-300">
                  {domain.brandTagline}
                </div>
              </div>
            </div>
            <p className="mt-5 max-w-sm text-neutral-400 text-[15px] leading-relaxed">
              {tagline}
            </p>

            <div className="mt-7 flex items-center gap-3">
              <a
                href={waUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="footer-whatsapp"
                className="w-10 h-10 rounded-full border border-white/15 grid place-items-center hover:bg-white/10 transition-colors"
              >
                <MessageCircle className="w-4 h-4" />
              </a>
              <a
                href={`mailto:${email}`}
                data-testid="footer-email"
                className="w-10 h-10 rounded-full border border-white/15 grid place-items-center hover:bg-white/10 transition-colors"
              >
                <Mail className="w-4 h-4" />
              </a>
            </div>
          </div>

          <div className="md:col-span-3">
            <div className="text-xs uppercase tracking-[0.22em] text-sky-300">
              {t("footer.services")}
            </div>
            <ul className="mt-5 space-y-3 text-[15px]" data-testid="footer-services-list">
              {orderedServices.map((slug) => {
                const det = SERVICE_DETAILS[slug];
                if (!det) return null;
                const label = pickLocalized(det.title, locale);
                const isFocused = slug === domain.focusedServiceSlug;
                return (
                  <li key={slug}>
                    <Link
                      to={`/services/${slug}`}
                      className={`hover:text-white ${isFocused ? "text-white font-medium" : ""}`}
                      data-testid={`footer-service-link-${slug}`}
                    >
                      {label}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="md:col-span-2">
            <div className="text-xs uppercase tracking-[0.22em] text-sky-300">
              {t("footer.company")}
            </div>
            <ul className="mt-5 space-y-3 text-[15px]">
              <li><a href="/#why" className="hover:text-white">{t("nav.why")}</a></li>
              <li><a href="/#process" className="hover:text-white">{t("nav.process")}</a></li>
              <li><a href="/blog" className="hover:text-white" data-testid="footer-blog-link">{t("nav.blog")}</a></li>
              <li><a href="/#contact" className="hover:text-white">{t("nav.contact")}</a></li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <div className="text-xs uppercase tracking-[0.22em] text-sky-300">
              {t("footer.legal")}
            </div>
            <ul className="mt-5 space-y-3 text-[15px]">
              <li><Link to="/impressum" className="hover:text-white" data-testid="footer-imprint-link">{t("footer.impressum")}</Link></li>
              <li><Link to="/datenschutz" className="hover:text-white" data-testid="footer-privacy-link">{t("footer.privacy")}</Link></li>
              <li><Link to="/admin/login" className="hover:text-white" data-testid="footer-admin-link">Admin</Link></li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-7 border-t border-white/10">
          <div
            className="text-[10px] uppercase tracking-[0.22em] text-sky-300 mb-3"
            data-testid="footer-network-label"
          >
            {locale === "en"
              ? "Mallorca Digital Network"
              : locale === "es"
              ? "Red Mallorca Digital"
              : "Mallorca Digital Network"}
          </div>
          <ul
            className="flex flex-wrap gap-x-5 gap-y-2 text-xs text-neutral-400"
            data-testid="footer-sister-domains"
          >
            {SISTER_DOMAINS.map((sd) => {
              const isCurrent = sd.host === domain.hostname;
              return (
                <li key={sd.host}>
                  <a
                    href={isCurrent ? "/" : `https://${sd.host}`}
                    target={isCurrent ? "_self" : "_blank"}
                    rel={isCurrent ? undefined : "noreferrer"}
                    className={`hover:text-white transition-colors ${
                      isCurrent ? "text-white font-medium" : ""
                    }`}
                    data-testid={`footer-sister-${sd.host.replace(/\./g, "-")}`}
                  >
                    {sd.label}
                  </a>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="mt-10 pt-7 border-t border-white/10 flex items-center justify-between flex-wrap gap-4 text-xs text-neutral-500">
          <div>{t("footer.copyright")}</div>
          <div>Palma · Pollença · Sóller · Artà · Cala d'Or</div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
